import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Pagination from '../components/Pagination';
import usePagination from '../hooks/usePagination';
import api from '../services/api';
import toast, { Toaster } from 'react-hot-toast';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import {
  DotsVerticalIcon,
  Pencil1Icon,
  TrashIcon,
  CameraIcon,
  PersonIcon,
  CheckCircledIcon,
  CrossCircledIcon,
  StarIcon,
} from '@radix-ui/react-icons';
import FacePhotosModal from '../components/FacePhotosModal';
import './Global.css';
import './UsersManagement.css';

const UsersManagement = () => {
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [positions, setPositions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('all'); // 'all', 'admin', 'user'
  const [statusFilter, setStatusFilter] = useState('all'); // 'all', 'active', 'inactive'
  const [efetivoFilter, setEfetivoFilter] = useState('all'); // 'all', 'yes', 'no'
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  // Pessoa com o modal de fotos faciais aberto ({id, name} ou null).
  const [facePhotosPerson, setFacePhotosPerson] = useState(null);
  const [deletingUser, setDeletingUser] = useState(false);

  // Ativar/inativar pela ação rápida do menu ⋮ (modal de confirmação próprio).
  const [statusTarget, setStatusTarget] = useState(null);
  const [savingStatus, setSavingStatus] = useState(false);

  // Seleção em massa guardada por id — precisa sobreviver à troca de página.
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  // Ação de lote aguardando confirmação: { efetivoCmcb: boolean } ou null.
  const [bulkAction, setBulkAction] = useState(null);
  const [applyingBulk, setApplyingBulk] = useState(false);

  // Modal de sincronização com SABM — estados: idle | running | done | disabled | error
  const [syncModal, setSyncModal] = useState({ open: false, state: 'idle', result: null, errorMsg: '' });

  // Paginação padrão (mesmo hook de todas as tabelas do sistema).
  const pagination = usePagination(filteredUsers, 10);
  const { setPage: setCurrentPage } = pagination;

  const openSyncModal = () => {
    setSyncModal({ open: true, state: 'idle', result: null, errorMsg: '' });
  };

  const closeSyncModal = () => {
    if (syncModal.state === 'running') return;
    setSyncModal({ open: false, state: 'idle', result: null, errorMsg: '' });
    if (syncModal.state === 'done' && syncModal.result?.updated?.length > 0) {
      loadUsers();
    }
  };

  const startSync = async () => {
    setSyncModal((s) => ({ ...s, state: 'running' }));
    try {
      const { data } = await api.post('/admin/legacy-sync/run');
      if (!data.legacyEnabled) {
        setSyncModal({ open: true, state: 'disabled', result: data, errorMsg: '' });
        return;
      }
      setSyncModal({ open: true, state: 'done', result: data, errorMsg: '' });
    } catch (err) {
      const msg = err.response?.data?.message || err.message;
      setSyncModal({ open: true, state: 'error', result: null, errorMsg: msg });
    }
  };

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    cpf: '',
    matricula: '',
    password: '',
    confirmPassword: '',
    role: 'user',
    additionalRoles: [], // funções extras — usuário escolhe qual usar no login
    militaryRank: '',
    positionIds: [],
    horasAulas: '',
    tetoIndividual: '', // vazio = usa o teto da categoria (tela Limites)
    isActive: true,
    efetivoCmcb: false,
    instrutorLote: false,
  });

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Função para formatar matrícula com máscara XXX.XXX-X-X
  const formatMatricula = (value) => {
    if (!value) return '';
    // Manter apenas números e X
    const clean = value.toUpperCase().replace(/[^0-9X]/g, '');
    if (clean.length <= 3) return clean;
    if (clean.length <= 6) return clean.slice(0, 3) + '.' + clean.slice(3);
    if (clean.length <= 7) return clean.slice(0, 3) + '.' + clean.slice(3, 6) + '-' + clean.slice(6);
    return clean.slice(0, 3) + '.' + clean.slice(3, 6) + '-' + clean.slice(6, 7) + '-' + clean.slice(7, 8);
  };

  // Função para remover máscara (manter apenas números e X)
  const unformatMatricula = (value) => {
    if (!value) return '';
    return value.toUpperCase().replace(/[.\-]/g, '');
  };

  const graduacoesPracas = [
    'Soldado',
    'Cabo',
    '3º Sargento',
    '2º Sargento',
    '1º Sargento',
    'Subtenente',
  ];

  const postosOficiais = [
    '2º Tenente',
    '1º Tenente',
    'Capitão',
    'Major',
    'Tenente-Coronel',
    'Coronel'
  ];

  // Função para obter prioridade de ordenação (quanto maior o número, mais alto o posto)
  const getRankPriority = (militaryRank) => {
    if (!militaryRank) return -1; // Usuários sem posto ficam por último

    const rankOrder = {
      'Coronel': 11,
      'Tenente-Coronel': 10,
      'Major': 9,
      'Capitão': 8,
      '1º Tenente': 7,
      '2º Tenente': 6,
      'Subtenente': 5,
      '1º Sargento': 4,
      '2º Sargento': 3,
      '3º Sargento': 2,
      'Cabo': 1,
      'Soldado': 0
    };

    return rankOrder[militaryRank] ?? -1;
  };

  // Função para remover acentos e normalizar texto
  const removeAccents = (str) => {
    if (!str) return '';
    return str.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
  };

  // O efetivo lotado no colégio é composto de militares — e a maioria deles
  // está cadastrada como FISCAL (é quem opera o ponto no colégio), não como
  // 'user'. Restringir a role='user' deixava justamente essa maioria de fora.
  // Ficam de fora só os perfis administrativos do sistema; quem já está
  // marcado segue selecionável, senão não haveria como desmarcar.
  const podeSerEfetivo = (user) =>
    user.efetivoCmcb === true ||
    (user.role !== 'admin' && user.role !== 'visualizador');

  useEffect(() => {
    loadUsers();
    loadPositions();
  }, []);

  const loadPositions = async () => {
    try {
      // Só funções do efetivo militar; as de civil ficam em /admin/civilians.
      const response = await api.get('/positions/active?personnelType=militar');
      setPositions(response.data);
    } catch (error) {
      console.error('Erro ao carregar funções:', error);
      toast.error('Erro ao carregar funções');
    }
  };

  useEffect(() => {
    // Filtrar usuários baseado na busca e no tipo
    const filtered = users.filter(user => {
      const searchNormalized = removeAccents(searchTerm);
      const matchesSearch = (
        removeAccents(user.name).includes(searchNormalized) ||
        removeAccents(user.matricula).includes(searchNormalized) ||
        removeAccents(user.email).includes(searchNormalized) ||
        removeAccents(user.militaryRank).includes(searchNormalized) ||
        removeAccents(user.positions?.map(p => p.name).join(' ') || '').includes(searchNormalized)
      );

      const matchesRole = roleFilter === 'all' || user.role === roleFilter;

      const matchesStatus =
        statusFilter === 'all' ||
        (statusFilter === 'active' ? user.isActive : !user.isActive);

      const matchesEfetivo =
        efetivoFilter === 'all' ||
        (efetivoFilter === 'yes' ? !!user.efetivoCmcb : !user.efetivoCmcb);

      return matchesSearch && matchesRole && matchesStatus && matchesEfetivo;
    });

    // Ordenar por posto/graduação (ordem decrescente - Coronel primeiro)
    const sorted = filtered.sort((a, b) => {
      const priorityA = getRankPriority(a.militaryRank);
      const priorityB = getRankPriority(b.militaryRank);

      // Ordenação decrescente
      if (priorityB !== priorityA) {
        return priorityB - priorityA;
      }

      // Se tiverem o mesmo posto, ordenar por nome
      return a.name.localeCompare(b.name);
    });

    setFilteredUsers(sorted);
    setCurrentPage(1); // Reset para primeira página ao buscar
  }, [searchTerm, users, roleFilter, statusFilter, efetivoFilter, setCurrentPage]);

  // Descarta da seleção quem sumiu da lista (exclusão, recarga) — sem isso o
  // lote enviaria ids fantasmas e o contador ficaria mentindo.
  useEffect(() => {
    setSelectedIds((prev) => {
      if (prev.size === 0) return prev;
      const vivos = new Set(users.filter((u) => prev.has(u.id)).map((u) => u.id));
      return vivos.size === prev.size ? prev : vivos;
    });
  }, [users]);

  const stats = useMemo(() => {
    const ativos = users.filter((u) => u.isActive).length;
    return {
      total: users.length,
      ativos,
      inativos: users.length - ativos,
      efetivoCmcb: users.filter((u) => u.efetivoCmcb).length,
    };
  }, [users]);

  const loadUsers = async () => {
    try {
      const response = await api.get('/users');
      // Esta tela é o efetivo militar (+ admins/fiscais/visualizadores).
      // O pessoal civil tem cadastro próprio em /admin/civilians.
      // O fallback pra 'militar' cobre a janela de deploy em que o backend
      // ainda não devolve personnelType — sem ele a tela ficaria vazia.
      const naoCivis = response.data.filter(
        (user) => (user.personnelType || 'militar') !== 'civil',
      );
      setUsers(naoCivis);
      setFilteredUsers(naoCivis);
    } catch (error) {
      console.error('Erro ao carregar usuários:', error);
      toast.error('Erro ao carregar usuários');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Só valida coincidência se o admin preencheu os dois campos.
    // Se digitou senha mas deixou "Confirmar Senha" em branco, aceita
    // — admin já está autenticado, exigir confirmação só atrapalhava.
    if (
      formData.password &&
      formData.confirmPassword &&
      formData.password !== formData.confirmPassword
    ) {
      toast.error('As senhas não coincidem!');
      return;
    }

    try {
      if (editingUser) {
        const updateData = { ...formData };

        // Remover máscara da matrícula antes de salvar
        if (updateData.matricula) {
          updateData.matricula = unformatMatricula(updateData.matricula);
        }

        // Remover máscara do CPF se foi preenchido
        if (updateData.cpf && updateData.cpf.trim() !== '') {
          updateData.cpf = updateData.cpf.replace(/\D/g, '');
        }

        // Se senha não foi preenchida, remover do update
        if (!formData.password) {
          delete updateData.password;
        }
        delete updateData.confirmPassword;

        // Marcação de militar: perfis administrativos do sistema não entram.
        if (formData.role === 'admin' || formData.role === 'visualizador') {
          delete updateData.efetivoCmcb;
          delete updateData.instrutorLote;
        }

        // Remover campos vazios ou undefined
        Object.keys(updateData).forEach(key => {
          if (updateData[key] === '' || updateData[key] === undefined || updateData[key] === null) {
            delete updateData[key];
          }
        });

        // Teto individual é a exceção da limpeza acima: apagar o campo tem de
        // chegar como null, senão o militar fica preso ao teto antigo.
        if (formData.role === 'user' || formData.role === 'fiscal') {
          updateData.tetoIndividual =
            formData.tetoIndividual === '' ? null : Number(formData.tetoIndividual);
        }

        await api.patch(`/users/${editingUser.id}`, updateData);
        toast.success('Usuário atualizado com sucesso!');
      } else {
        // Preparar dados para criação
        const userData = { ...formData };
        delete userData.confirmPassword; // Remover confirmPassword antes de enviar
        if (formData.role === 'admin' || formData.role === 'visualizador') {
          delete userData.efetivoCmcb; // marcação exclusiva do efetivo militar
          delete userData.instrutorLote;
        }

        // Criar email baseado no tipo de usuário
        if (formData.role === 'fiscal') {
          // Para fiscais, remover máscara da matrícula antes de salvar
          const matriculaSemMascara = unformatMatricula(formData.matricula);
          userData.matricula = matriculaSemMascara;

          // Gerar email baseado na matrícula apenas se não foi preenchido
          if (!formData.email || formData.email.trim() === '') {
            userData.email = matriculaSemMascara + '@cmcb.ce.gov.br';
          } else {
            userData.email = formData.email.toLowerCase();
          }

          // Remover máscara do CPF se foi preenchido
          if (formData.cpf && formData.cpf.trim() !== '') {
            userData.cpf = formData.cpf.replace(/\D/g, '');
          }

          // Fiscais têm senha, militaryRank e positionIds
          // Não deletar esses campos
        } else if (['admin', 'visualizador'].includes(formData.role)) {
          // Para admin e visualizador, usar o email digitado se fornecido
          if (formData.email && formData.email.trim() !== '') {
            userData.email = formData.email.toLowerCase();
          }

          // Remover máscara do CPF se foi preenchido
          if (formData.cpf && formData.cpf.trim() !== '') {
            userData.cpf = formData.cpf.replace(/\D/g, '');
          }

          // Remover campos que não devem ser enviados
          delete userData.militaryRank;
          delete userData.positionIds;
          delete userData.horasAulas;
          delete userData.tetoIndividual;
          delete userData.matricula;
        } else {
          // Para usuário militar, remover máscara da matrícula antes de salvar
          const matriculaSemMascara = unformatMatricula(formData.matricula);
          userData.matricula = matriculaSemMascara;

          // Gerar email baseado na matrícula apenas se não foi preenchido
          if (!formData.email || formData.email.trim() === '') {
            userData.email = matriculaSemMascara + '@cmcb.ce.gov.br';
          } else {
            userData.email = formData.email;
          }

          // Remover máscara do CPF se foi preenchido
          if (formData.cpf && formData.cpf.trim() !== '') {
            userData.cpf = formData.cpf.replace(/\D/g, '');
          }

          delete userData.password; // Militares não têm senha
        }

        // Remover campos vazios ou undefined
        Object.keys(userData).forEach(key => {
          if (userData[key] === '' || userData[key] === undefined || userData[key] === null) {
            delete userData[key];
          }
        });

        await api.post('/users', userData);
        toast.success('Usuário criado com sucesso!');
      }
      handleCloseModal();
      loadUsers();
    } catch (error) {
      console.error('Erro ao salvar usuário:', error);
      toast.error('Erro ao salvar usuário: ' + (error.response?.data?.message || error.message));
    }
  };

  const handleEdit = (user) => {
    setEditingUser(user);
    setFormData({
      name: user.name,
      email: user.email || '',
      cpf: user.cpf || '',
      matricula: formatMatricula(user.matricula || ''),
      password: '',
      confirmPassword: '',
      role: user.role,
      additionalRoles: Array.isArray(user.additionalRoles) ? user.additionalRoles : [],
      militaryRank: user.militaryRank || '',
      positionIds: user.positions?.map(p => p.id) || [],
      horasAulas: user.horasAulas || '',
      tetoIndividual: user.tetoIndividual ?? '',
      isActive: user.isActive !== false,
      efetivoCmcb: !!user.efetivoCmcb,
      instrutorLote: !!user.instrutorLote,
    });
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingUser(null);
    setFormData({
      name: '',
      email: '',
      cpf: '',
      matricula: '',
      password: '',
      confirmPassword: '',
      role: 'user',
      additionalRoles: [],
      militaryRank: '',
      positionIds: [],
      horasAulas: '',
      tetoIndividual: '',
      isActive: true,
      efetivoCmcb: false,
      instrutorLote: false,
    });
    setShowPassword(false);
    setShowConfirmPassword(false);
  };

  const openDeleteModal = (user) => {
    setUserToDelete(user);
    setShowDeleteModal(true);
  };

  const closeDeleteModal = () => {
    if (deletingUser) return;
    setShowDeleteModal(false);
    setUserToDelete(null);
  };

  const handleConfirmDelete = async () => {
    if (!userToDelete) return;

    setDeletingUser(true);
    try {
      await api.delete(`/users/${userToDelete.id}`);
      toast.success('Usuário excluído com sucesso!');
      loadUsers();
    } catch (error) {
      console.error('Erro ao excluir usuário:', error);
      toast.error('Erro ao excluir usuário: ' + (error.response?.data?.message || error.message));
    } finally {
      setDeletingUser(false);
      setShowDeleteModal(false);
      setUserToDelete(null);
    }
  };

  // ---------- ativar / inativar ----------

  const closeStatusModal = () => {
    if (savingStatus) return;
    setStatusTarget(null);
  };

  const handleConfirmStatus = async () => {
    if (!statusTarget) return;
    const novoStatus = !statusTarget.isActive;

    setSavingStatus(true);
    try {
      await api.patch(`/users/${statusTarget.id}`, { isActive: novoStatus });
      toast.success(
        novoStatus
          ? `${statusTarget.name} foi reativado.`
          : `${statusTarget.name} foi inativado.`,
      );
      loadUsers();
    } catch (error) {
      console.error('Erro ao alterar status:', error);
      toast.error(
        'Erro ao alterar status: ' + (error.response?.data?.message || error.message),
      );
    } finally {
      setSavingStatus(false);
      setStatusTarget(null);
    }
  };

  // ---------- seleção em massa ----------

  const toggleSelected = (userId) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(userId)) next.delete(userId);
      else next.add(userId);
      return next;
    });
  };

  const applyBulkEfetivo = async () => {
    if (!bulkAction) return;
    const userIds = Array.from(selectedIds);
    if (userIds.length === 0) {
      setBulkAction(null);
      return;
    }

    setApplyingBulk(true);
    try {
      const { data } = await api.patch('/users/bulk/efetivo-cmcb', {
        userIds,
        efetivoCmcb: bulkAction.efetivoCmcb,
      });
      const qtd = data?.atualizados ?? userIds.length;
      toast.success(
        bulkAction.efetivoCmcb
          ? `${qtd} militar(es) marcado(s) como efetivo do CMCB (cota de 48h).`
          : `${qtd} militar(es) removido(s) do efetivo do CMCB.`,
      );
      setSelectedIds(new Set());
      loadUsers();
    } catch (error) {
      console.error('Erro na marcação em lote:', error);
      const msg = error.response?.data?.message;
      toast.error(
        'Erro ao aplicar em lote: ' +
          (Array.isArray(msg) ? msg.join(', ') : msg || error.message),
      );
    } finally {
      setApplyingBulk(false);
      setBulkAction(null);
    }
  };

  const exportUsers = () => {
    try {
      if (filteredUsers.length === 0) {
        toast.error('Nenhum usuário para exportar');
        return;
      }

      // Preparar dados para exportação
      const dataToExport = filteredUsers.map(user => ({
        'Matrícula': formatMatricula(user.matricula) || '-',
        'Nome Completo': user.name,
        'Posto/Graduação': user.militaryRank || (user.role === 'admin' ? 'Administrador' : '-'),
        'Função': user.positions?.map(p => p.name).join(', ') || '-',
        'Horas Aulas': user.horasAulas || '-',
        'Teto individual': user.tetoIndividual ?? '-',
        'E-mail': user.email,
        'Status': user.isActive ? 'Ativo' : 'Inativo',
        'Efetivo CMCB': user.efetivoCmcb ? 'Sim' : 'Não',
        'Instrutor lançamento em massa': user.instrutorLote ? 'Sim' : 'Não',
        'Tipo': user.role === 'admin' ? 'Administrador' : 'Usuário'
      }));

      // Criar CSV
      const headers = Object.keys(dataToExport[0]);
      const csvContent = [
        headers.join(','),
        ...dataToExport.map(row =>
          headers.map(header => {
            const value = row[header];
            // Escapar valores que contêm vírgula ou aspas
            if (typeof value === 'string' && (value.includes(',') || value.includes('"'))) {
              return `"${value.replace(/"/g, '""')}"`;
            }
            return value;
          }).join(',')
        )
      ].join('\n');

      // Criar blob e fazer download
      const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const now = new Date();
      const dateStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
      link.download = `usuarios_cmcb_${dateStr}.csv`;

      document.body.appendChild(link);
      link.click();

      // Cleanup
      setTimeout(() => {
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }, 100);

      toast.success(`${filteredUsers.length} usuário(s) exportado(s) com sucesso!`);
    } catch (error) {
      console.error('Erro ao exportar usuários:', error);
      toast.error('Erro ao exportar usuários: ' + error.message);
    }
  };

  const currentUsers = pagination.pageItems;

  // "Selecionar todos os visíveis" = os selecionáveis da página atual.
  const visiveisSelecionaveis = currentUsers.filter(podeSerEfetivo);
  const visiveisMarcados = visiveisSelecionaveis.filter((u) => selectedIds.has(u.id));
  const todosVisiveisMarcados =
    visiveisSelecionaveis.length > 0 &&
    visiveisMarcados.length === visiveisSelecionaveis.length;
  const algumVisivelMarcado =
    visiveisMarcados.length > 0 && !todosVisiveisMarcados;

  const toggleTodosVisiveis = () => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (todosVisiveisMarcados) {
        visiveisSelecionaveis.forEach((u) => next.delete(u.id));
      } else {
        visiveisSelecionaveis.forEach((u) => next.add(u.id));
      }
      return next;
    });
  };

  const filtradosSelecionaveis = filteredUsers.filter(podeSerEfetivo);

  const selecionarTodosDoFiltro = () => {
    // Soma à seleção que já existe: o fluxo real é filtrar por um pedaço da
    // relação, selecionar, filtrar por outro e selecionar de novo. Substituir
    // apagaria em silêncio o que foi marcado nos filtros anteriores.
    setSelectedIds((prev) => {
      const next = new Set(prev);
      filtradosSelecionaveis.forEach((u) => next.add(u.id));
      return next;
    });
  };

  // Nomes de quem será afetado no lote — o modal precisa listar antes de aplicar.
  const selecionados = users.filter((u) => selectedIds.has(u.id));

  if (loading) return (
    <>
      <Navbar />
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Carregando usuários...</p>
      </div>
    </>
  );

  return (
    <>
      <Toaster position="top-right" />
      <Navbar />
      <div className="container">
        <div className="page-header-new">
          <div>
            <h1 style={{ margin: 0 }}>Gerenciamento de Usuários</h1>
            <p className="subtitle">
              {filteredUsers.length} {filteredUsers.length === 1 ? 'usuário' : 'usuários'} cadastrado{filteredUsers.length === 1 ? '' : 's'}
              {' · '}o pessoal civil fica em <Link to="/admin/civilians">Civis</Link>
            </p>
          </div>
          <div className="header-actions">
            <button
              type="button"
              className="btn-sync"
              onClick={openSyncModal}
              title="Sincroniza User.isActive com pessoa_bm.pts_codigo do SABM"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: 6 }}>
                <polyline points="23 4 23 10 17 10"></polyline>
                <polyline points="1 20 1 14 7 14"></polyline>
                <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"></path>
              </svg>
              Sincronizar com SABM
            </button>
            <button type="button" className="btn-secondary" onClick={exportUsers}>
              Exportar Usuários
            </button>
            <button type="button" className="btn-primary" onClick={() => setShowModal(true)}>
              + Novo Usuário
            </button>
          </div>
        </div>

        <div className="um-stats">
          <div className="um-stat">
            <span className="um-stat__icon">
              <PersonIcon width={18} height={18} />
            </span>
            <div>
              <strong>{stats.total}</strong>
              <span>Cadastrados</span>
            </div>
          </div>
          <div className="um-stat um-stat--active">
            <span className="um-stat__icon">
              <CheckCircledIcon width={18} height={18} />
            </span>
            <div>
              <strong>{stats.ativos}</strong>
              <span>Ativos</span>
            </div>
          </div>
          <div className="um-stat um-stat--inactive">
            <span className="um-stat__icon">
              <CrossCircledIcon width={18} height={18} />
            </span>
            <div>
              <strong>{stats.inativos}</strong>
              <span>Inativos</span>
            </div>
          </div>
          <div className="um-stat um-stat--cmcb">
            <span className="um-stat__icon">
              <StarIcon width={18} height={18} />
            </span>
            <div>
              <strong>{stats.efetivoCmcb}</strong>
              <span>Efetivo do CMCB (48h)</span>
            </div>
          </div>
        </div>

        <div className="table-controls">
          <div className="search-bar">
            <input
              type="text"
              placeholder="Buscar usuários..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
          </div>

          <div className="um-filters">
            <div className="role-filter">
              <label htmlFor="um-role-filter">Tipo:</label>
              <select
                id="um-role-filter"
                value={roleFilter}
                onChange={(e) => {
                  setRoleFilter(e.target.value);
                  setCurrentPage(1);
                }}
                className="role-select"
              >
                <option value="all">Todos</option>
                <option value="admin">Administrador</option>
                <option value="fiscal">Fiscal</option>
                <option value="visualizador">Visualizador</option>
                <option value="user">Militar</option>
              </select>
            </div>

            <div className="role-filter">
              <label htmlFor="um-status-filter">Status:</label>
              <select
                id="um-status-filter"
                value={statusFilter}
                onChange={(e) => {
                  setStatusFilter(e.target.value);
                  setCurrentPage(1);
                }}
                className="role-select"
              >
                <option value="all">Todos</option>
                <option value="active">Ativos</option>
                <option value="inactive">Inativos</option>
              </select>
            </div>

            <div className="role-filter">
              <label htmlFor="um-efetivo-filter">Efetivo do CMCB:</label>
              <select
                id="um-efetivo-filter"
                value={efetivoFilter}
                onChange={(e) => {
                  setEfetivoFilter(e.target.value);
                  setCurrentPage(1);
                }}
                className="role-select"
              >
                <option value="all">Todos</option>
                <option value="yes">Só efetivo CMCB</option>
                <option value="no">Fora do efetivo</option>
              </select>
            </div>
          </div>
        </div>

        <div className="table-wrapper um-table-wrapper">
          <table className="modern-table um-table">
            <thead>
              <tr>
                <th className="um-select-cell">
                  <input
                    type="checkbox"
                    className="um-check"
                    checked={todosVisiveisMarcados}
                    ref={(el) => {
                      // Estado "traço": parte da página marcada.
                      if (el) el.indeterminate = algumVisivelMarcado;
                    }}
                    onChange={toggleTodosVisiveis}
                    disabled={visiveisSelecionaveis.length === 0}
                    aria-label="Selecionar todos os militares visíveis"
                    title="Selecionar todos os militares visíveis nesta página"
                  />
                </th>
                <th>Matrícula</th>
                <th>Nome Completo</th>
                <th>Posto/Graduação</th>
                <th>Função</th>
                <th className="text-center">Horas Aulas</th>
                {/* Duas marcas convivem aqui: efetivo do CMCB (48h) e
                    instrutor do lançamento em massa. */}
                <th className="text-center">Lançamento em massa</th>
                <th className="text-center">Status</th>
                <th className="text-center">Ações</th>
              </tr>
            </thead>
            <tbody>
              {currentUsers.length === 0 ? (
                <tr>
                  <td colSpan="9" className="text-center empty-state">
                    <strong>
                      {searchTerm ? 'Nenhum usuário encontrado' : 'Nenhum usuário cadastrado'}
                    </strong>
                    {searchTerm
                      ? 'Revise a busca ou limpe os filtros de perfil, status e efetivo.'
                      : 'Cadastre o primeiro usuário ou importe do SABM.'}
                  </td>
                </tr>
              ) : (
                currentUsers.map((user) => (
                  <tr
                    key={user.id}
                    className={`${user.isActive ? '' : 'um-row--inactive'} ${
                      selectedIds.has(user.id) ? 'um-row--selected' : ''
                    }`.trim()}
                  >
                    <td className="um-select-cell">
                      {podeSerEfetivo(user) ? (
                        <input
                          type="checkbox"
                          className="um-check"
                          checked={selectedIds.has(user.id)}
                          onChange={() => toggleSelected(user.id)}
                          aria-label={`Selecionar ${user.name}`}
                        />
                      ) : null}
                    </td>
                    <td><span className="matricula-text">{formatMatricula(user.matricula) || '-'}</span></td>
                    <td className="cell-primary">{user.name}</td>
                    <td>
                      {user.militaryRank ? (
                        <span className="badge badge-rank">
                          {user.militaryRank}
                        </span>
                      ) : (
                        <span className={`badge ${
                          user.role === 'admin' ? 'badge-admin' :
                          user.role === 'fiscal' ? 'badge-fiscal' :
                          user.role === 'visualizador' ? 'badge-visualizador' :
                          'badge-user'
                        }`}>
                          {user.role === 'admin' ? 'Admin' :
                           user.role === 'fiscal' ? 'Fiscal' :
                           user.role === 'visualizador' ? 'Visualizador' : '-'}
                        </span>
                      )}
                    </td>
                    <td>{user.positions?.map(p => p.name).join(', ') || '-'}</td>
                    <td className="text-center">
                      {user.horasAulas ? `${user.horasAulas}h` : '-'}
                      {user.tetoIndividual != null && (
                        <span
                          className="um-badge-teto"
                          title="Teto individual: substitui o teto da categoria deste militar no ciclo"
                        >
                          teto {user.tetoIndividual}h
                        </span>
                      )}
                    </td>
                    <td className="text-center">
                      {user.efetivoCmcb && (
                        <span className="um-badge-cmcb" title="Militar lotado no CMCB — teto do colégio no ciclo (tela Limites)">
                          CMCB
                        </span>
                      )}
                      {user.instrutorLote && (
                        <span
                          className="um-badge-instrutor"
                          title="Instrutor autorizado a receber lançamento em massa"
                        >
                          Instrutor lote
                        </span>
                      )}
                      {!user.efetivoCmcb && !user.instrutorLote && <span className="um-dash">—</span>}
                    </td>
                    <td className="text-center">
                      <span className={`badge ${user.isActive ? 'badge-success' : 'badge-danger'}`}>
                        {user.isActive ? 'Ativo' : 'Inativo'}
                      </span>
                    </td>
                    <td className="text-center">
                      <DropdownMenu.Root>
                        <DropdownMenu.Trigger asChild>
                          <button className="icon-button" aria-label="Ações">
                            <DotsVerticalIcon />
                          </button>
                        </DropdownMenu.Trigger>

                        <DropdownMenu.Portal>
                          <DropdownMenu.Content className="dropdown-content" sideOffset={5}>
                            <DropdownMenu.Item className="dropdown-item" onSelect={() => handleEdit(user)}>
                              <Pencil1Icon /> Editar
                            </DropdownMenu.Item>

                            <DropdownMenu.Item
                              className="dropdown-item"
                              onSelect={() => setFacePhotosPerson({ id: user.id, name: user.name })}
                            >
                              <CameraIcon /> Fotos faciais
                            </DropdownMenu.Item>

                            <DropdownMenu.Item
                              className="dropdown-item"
                              onSelect={() => setStatusTarget(user)}
                            >
                              {user.isActive ? (
                                <>
                                  <CrossCircledIcon /> Inativar
                                </>
                              ) : (
                                <>
                                  <CheckCircledIcon /> Ativar
                                </>
                              )}
                            </DropdownMenu.Item>

                            {user.role !== 'admin' && (
                              <>
                                <DropdownMenu.Separator className="dropdown-separator" />
                                <DropdownMenu.Item
                                  className="dropdown-item dropdown-item-danger"
                                  onSelect={() => openDeleteModal(user)}
                                >
                                  <TrashIcon /> Excluir
                                </DropdownMenu.Item>
                              </>
                            )}
                          </DropdownMenu.Content>
                        </DropdownMenu.Portal>
                      </DropdownMenu.Root>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {selectedIds.size > 0 && (
          <div className="um-bulkbar" role="region" aria-label="Ações em massa">
            <div className="um-bulkbar__info">
              <strong>{selectedIds.size} selecionado{selectedIds.size === 1 ? '' : 's'}</strong>
              <span>
                A seleção continua valendo ao trocar de página ou de filtro.
                {selectedIds.size < filtradosSelecionaveis.length && (
                  <>
                    {' '}
                    <button type="button" className="um-linkbtn" onClick={selecionarTodosDoFiltro}>
                      Selecionar os {filtradosSelecionaveis.length} do filtro atual
                    </button>
                  </>
                )}
              </span>
            </div>
            <div className="um-bulkbar__actions">
              <button
                type="button"
                className="btn-secondary btn-sm"
                onClick={() => setSelectedIds(new Set())}
              >
                Limpar seleção
              </button>
              <button
                type="button"
                className="btn-secondary btn-sm"
                onClick={() => setBulkAction({ efetivoCmcb: false })}
              >
                Remover do efetivo
              </button>
              <button
                type="button"
                className="btn-primary btn-sm"
                onClick={() => setBulkAction({ efetivoCmcb: true })}
              >
                Marcar como efetivo do CMCB
              </button>
            </div>
          </div>
        )}

        <Pagination
          id="users"
          page={pagination.page}
          totalPages={pagination.totalPages}
          total={pagination.total}
          from={pagination.from}
          to={pagination.to}
          pageSize={pagination.pageSize}
          onPageChange={pagination.setPage}
          onPageSizeChange={pagination.setPageSize}
          itemLabel="usuários"
        />

        {showModal && (
          <div className="modal-overlay" onClick={handleCloseModal}>
            <div className="modal" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>{editingUser ? 'Editar Usuário' : 'Novo Usuário'}</h2>
                <button className="modal-close" onClick={handleCloseModal}>
                  ×
                </button>
              </div>

              <form onSubmit={handleSubmit} className="form">
                <div className="form-group">
                  <label>Tipo de Usuário (principal) *</label>
                  <select
                    value={formData.role}
                    onChange={(e) => setFormData({
                      ...formData,
                      role: e.target.value,
                      // a principal não pode também estar nas adicionais
                      additionalRoles: (formData.additionalRoles || []).filter((r) => r !== e.target.value),
                    })}
                  >
                    <option value="user">Militar</option>
                    <option value="admin">Administrador</option>
                    <option value="fiscal">Fiscal</option>
                    <option value="visualizador">Visualizador</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>Funções adicionais</label>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', padding: '8px 0' }}>
                    {[
                      { v: 'user', l: 'Militar' },
                      { v: 'fiscal', l: 'Fiscal' },
                      { v: 'admin', l: 'Administrador' },
                      { v: 'visualizador', l: 'Visualizador' },
                    ]
                      .filter((opt) => opt.v !== formData.role)
                      .map((opt) => {
                        const checked = (formData.additionalRoles || []).includes(opt.v);
                        return (
                          <label key={opt.v} style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={(e) => {
                                const set = new Set(formData.additionalRoles || []);
                                if (e.target.checked) set.add(opt.v);
                                else set.delete(opt.v);
                                setFormData({ ...formData, additionalRoles: Array.from(set) });
                              }}
                            />
                            {opt.l}
                          </label>
                        );
                      })}
                  </div>
                  <small style={{ color: '#6b7280', fontSize: '12px' }}>
                    Marque outras funções que este usuário também pode exercer. No login ele escolhe com qual entrar.
                  </small>
                </div>

                <div className="form-group">
                  <label>Nome Completo *</label>
                  <input
                    type="text"
                    value={formData.name}
                    style={{ textTransform: 'uppercase' }}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value.toUpperCase() })}
                    required
                  />
                </div>

                {/* Campos para Fiscais - Matrícula é obrigatória */}
                {((!editingUser && formData.role === 'fiscal') ||
                  (editingUser && editingUser.role === 'fiscal')) && (
                  <>
                    <div className="form-group">
                      <label>Matrícula *</label>
                      <input
                        type="text"
                        value={formData.matricula}
                        onChange={(e) => {
                          const formatted = formatMatricula(e.target.value);
                          setFormData({ ...formData, matricula: formatted });
                        }}
                        placeholder="Ex: 300.343-1-7"
                        maxLength={13}
                        required
                      />
                      <small className="form-hint">
                        A matrícula pode ser usada como login
                      </small>
                    </div>

                    <div className="form-group">
                      <label>Email</label>
                      <input
                        type="email"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        placeholder="email@exemplo.com"
                      />
                      <small className="form-hint">
                        Opcional - Se deixado em branco, será gerado baseado na matrícula
                      </small>
                    </div>

                    <div className="form-group">
                      <label>CPF</label>
                      <input
                        type="text"
                        value={formData.cpf}
                        onChange={(e) => {
                          let value = e.target.value.replace(/\D/g, '');

                          // Aplica a máscara XXX.XXX.XXX-XX
                          if (value.length > 0) {
                            if (value.length <= 3) {
                              value = value;
                            } else if (value.length <= 6) {
                              value = value.slice(0, 3) + '.' + value.slice(3);
                            } else if (value.length <= 9) {
                              value = value.slice(0, 3) + '.' + value.slice(3, 6) + '.' + value.slice(6);
                            } else {
                              value = value.slice(0, 3) + '.' + value.slice(3, 6) + '.' + value.slice(6, 9) + '-' + value.slice(9, 11);
                            }
                          }

                          setFormData({ ...formData, cpf: value });
                        }}
                        placeholder="000.000.000-00"
                        maxLength={14}
                      />
                      <small className="form-hint">
                        Opcional
                      </small>
                    </div>
                  </>
                )}

                {/* Campos para Administradores e Visualizador - SEM matrícula */}
                {((!editingUser && ['admin', 'visualizador'].includes(formData.role)) ||
                  (editingUser && ['admin', 'visualizador'].includes(editingUser.role))) && (
                  <>
                    <div className="form-group">
                      <label>Email</label>
                      <input
                        type="email"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        placeholder="email@exemplo.com"
                      />
                      <small className="form-hint">
                        Opcional
                      </small>
                    </div>

                    <div className="form-group">
                      <label>CPF</label>
                      <input
                        type="text"
                        value={formData.cpf}
                        onChange={(e) => {
                          let value = e.target.value.replace(/\D/g, '');

                          // Aplica a máscara XXX.XXX.XXX-XX
                          if (value.length > 0) {
                            if (value.length <= 3) {
                              value = value;
                            } else if (value.length <= 6) {
                              value = value.slice(0, 3) + '.' + value.slice(3);
                            } else if (value.length <= 9) {
                              value = value.slice(0, 3) + '.' + value.slice(3, 6) + '.' + value.slice(6);
                            } else {
                              value = value.slice(0, 3) + '.' + value.slice(3, 6) + '.' + value.slice(6, 9) + '-' + value.slice(9, 11);
                            }
                          }

                          setFormData({ ...formData, cpf: value });
                        }}
                        placeholder="000.000.000-00"
                        maxLength={14}
                      />
                      <small className="form-hint">
                        Opcional
                      </small>
                    </div>
                  </>
                )}

                {/* Campo de Matrícula para Usuários Militares */}
                {(!editingUser && formData.role === 'user') || (editingUser && editingUser.role === 'user') ? (
                  <>
                    <div className="form-group">
                      <label>Matrícula *</label>
                      <input
                        type="text"
                        value={formData.matricula}
                        onChange={(e) => {
                          const formatted = formatMatricula(e.target.value);
                          setFormData({ ...formData, matricula: formatted });
                        }}
                        placeholder="Ex: 300.343-1-7"
                        maxLength={13}
                        required
                      />
                      <small className="form-hint">
                        A matrícula será usada como login do usuário
                      </small>
                    </div>

                    <div className="form-group">
                      <label>Email</label>
                      <input
                        type="email"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        placeholder="exemplo@email.com"
                      />
                      <small className="form-hint">
                        Opcional - Se deixado em branco, será gerado automaticamente
                      </small>
                    </div>

                    <div className="form-group">
                      <label>CPF</label>
                      <input
                        type="text"
                        value={formData.cpf}
                        onChange={(e) => {
                          let value = e.target.value.replace(/\D/g, '');

                          if (value.length <= 11) {
                            if (value.length > 9) {
                              value = value.slice(0, 3) + '.' + value.slice(3, 6) + '.' + value.slice(6, 9) + '-' + value.slice(9, 11);
                            } else if (value.length > 6) {
                              value = value.slice(0, 3) + '.' + value.slice(3, 6) + '.' + value.slice(6);
                            } else if (value.length > 3) {
                              value = value.slice(0, 3) + '.' + value.slice(3);
                            }
                          }

                          setFormData({ ...formData, cpf: value });
                        }}
                        placeholder="000.000.000-00"
                        maxLength={14}
                      />
                      <small className="form-hint">
                        Opcional
                      </small>
                    </div>
                  </>
                ) : null}

                {/* Campos de Senha - Criação e Edição */}
                  <>
                    <div className="form-group">
                      <label>
                        Senha {!editingUser && formData.role !== 'user' && '*'}
                        {editingUser && <small style={{color: '#6b7280', fontSize: '12px', marginLeft: '8px'}}>(deixe em branco para não alterar)</small>}
                        {!editingUser && formData.role === 'user' && <small style={{color: '#6b7280', fontSize: '12px', marginLeft: '8px'}}>(padrão: matrícula)</small>}
                      </label>
                      <div className="password-input-wrapper">
                        <input
                          type={showPassword ? "text" : "password"}
                          value={formData.password}
                          onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                          required={!editingUser && formData.role !== 'user'}
                          minLength={6}
                          placeholder={editingUser ? "Digite nova senha (opcional)" : (formData.role === 'user' ? "Deixe em branco para usar a matrícula" : "Senha de acesso ao sistema")}
                          style={{width: '100%', paddingRight: '45px'}}
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="password-toggle"
                          tabIndex="-1"
                        >
                          {showPassword ? (
                            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/><path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
                          ) : (
                            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                          )}
                        </button>
                      </div>
                    </div>

                    <div className="form-group">
                      <label>
                        Confirmar Senha {!editingUser && '*'}
                      </label>
                      <div className="password-input-wrapper">
                        <input
                          type={showConfirmPassword ? "text" : "password"}
                          value={formData.confirmPassword}
                          onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                          required={!editingUser && formData.role !== 'user'}
                          minLength={6}
                          placeholder={editingUser ? "Confirme a nova senha (opcional)" : (formData.role === 'user' ? "Confirme a senha (opcional)" : "Digite a senha novamente")}
                          style={{width: '100%', paddingRight: '45px'}}
                        />
                        <button
                          type="button"
                          onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                          className="password-toggle"
                          tabIndex="-1"
                        >
                          {showConfirmPassword ? (
                            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/><path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
                          ) : (
                            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                          )}
                        </button>
                      </div>
                    </div>
                  </>

                {/* Campos para Fiscais e Usuários Militares */}
                {((!editingUser && ['user', 'fiscal'].includes(formData.role)) ||
                  (editingUser && ['user', 'fiscal'].includes(editingUser.role))) && (
                  <>
                    <div className="form-group">
                      <label>Posto/Graduação</label>
                      <select
                        value={formData.militaryRank}
                        onChange={(e) => setFormData({ ...formData, militaryRank: e.target.value })}
                      >
                        <option value="">Selecione...</option>
                        <optgroup label="Graduações de Praças">
                          {graduacoesPracas.map(grad => (
                            <option key={grad} value={grad}>{grad}</option>
                          ))}
                        </optgroup>
                        <optgroup label="Postos de Oficiais">
                          {postosOficiais.map(posto => (
                            <option key={posto} value={posto}>{posto}</option>
                          ))}
                        </optgroup>
                      </select>
                    </div>

                    {formData.role === 'user' && (
                    <div className="form-group">
                      <label>Funções</label>
                      <div style={{
                        border: '1px solid #d1d5db',
                        borderRadius: '8px',
                        maxHeight: '160px',
                        overflowY: 'auto',
                        padding: '8px'
                      }}>
                        {positions.length === 0 ? (
                          <span style={{ color: '#9ca3af', fontSize: '14px' }}>Nenhuma função cadastrada</span>
                        ) : (
                          positions.map(position => (
                            <label
                              key={position.id}
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                                padding: '6px 8px',
                                cursor: 'pointer',
                                borderRadius: '4px',
                                transition: 'background-color 0.15s',
                                fontSize: '14px',
                              }}
                              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f3f4f6'}
                              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                            >
                              <input
                                type="checkbox"
                                checked={formData.positionIds.includes(position.id)}
                                onChange={(e) => {
                                  const newIds = e.target.checked
                                    ? [...formData.positionIds, position.id]
                                    : formData.positionIds.filter(id => id !== position.id);
                                  setFormData({ ...formData, positionIds: newIds });
                                }}
                                style={{ margin: 0 }}
                              />
                              {position.name}
                            </label>
                          ))
                        )}
                      </div>
                      <small className="form-hint">
                        Gerencie as funções na aba "Funções" do menu
                      </small>
                    </div>
                    )}

                    {formData.role === 'user' && (
                      <div className="form-row">
                        <div className="form-group">
                          <label>Horas Aulas</label>
                          <input
                            type="number"
                            value={formData.horasAulas}
                            onChange={(e) => setFormData({ ...formData, horasAulas: parseInt(e.target.value) || '' })}
                            min={1}
                            max={60}
                            placeholder="Quantidade de horas aulas"
                          />
                          <small className="form-hint">
                            Meta do instrutor no lançamento em massa. Não é teto.
                          </small>
                        </div>
                      </div>
                    )}

                    {/* Teto individual vale para quem bate ponto: militar e fiscal
                        (a maior parte do efetivo do CMCB é fiscal). Mesma faixa do
                        backend: 1..80. */}
                    {(formData.role === 'user' || formData.role === 'fiscal') && (
                      <div className="form-group">
                        <label>Teto individual de horas (opcional)</label>
                        <input
                          type="number"
                          value={formData.tetoIndividual}
                          onChange={(e) => {
                            const v = e.target.value;
                            setFormData({ ...formData, tetoIndividual: v === '' ? '' : parseInt(v) });
                          }}
                          min={1}
                          max={80}
                          placeholder="Vazio = teto da categoria"
                        />
                        <small className="form-hint">
                          Entre 1 e 80. Substitui o teto da categoria só para este militar e
                          limita o total dele no ciclo (ainda sujeito ao teto geral). Os tetos
                          padrão ficam na tela Limites.
                        </small>
                      </div>
                    )}

                    {(formData.role === 'user' || formData.role === 'fiscal') && (
                      <div className="form-group">
                        <label className="um-checkbox">
                          <input
                            type="checkbox"
                            checked={formData.efetivoCmcb}
                            onChange={(e) =>
                              setFormData({ ...formData, efetivoCmcb: e.target.checked })
                            }
                          />
                          <span>
                            <strong>Efetivo do CMCB</strong> (lotado no colégio — teto do colégio no ciclo)
                          </span>
                        </label>
                        <small className="form-hint">
                          Quem é do efetivo entra na categoria "colégio" em todos os registros
                          (teto definido na tela Limites; ciclo do dia 16 ao dia 15) e aparece na
                          tela de Lançamento em massa. O campo "Horas Aulas" continua valendo só
                          para quem ministra hora-aula — a marcação não o altera.
                        </small>
                      </div>
                    )}

                    {(formData.role === 'user' || formData.role === 'fiscal') && (
                      <div className="form-group">
                        <label className="um-checkbox">
                          <input
                            type="checkbox"
                            checked={formData.instrutorLote}
                            onChange={(e) =>
                              setFormData({ ...formData, instrutorLote: e.target.checked })
                            }
                          />
                          <span>
                            <strong>Instrutor com lançamento em massa</strong> (aba "Instrutores"
                            da tela de Lançamento em massa)
                          </span>
                        </label>
                        <small className="form-hint">
                          Lista nominal e curta: só quem está marcado aqui aparece naquela aba, e
                          o sistema <strong>recusa</strong> lançamento em lote para qualquer outro
                          militar. A marcação <strong>não muda o teto</strong> dele — o teto vem da
                          categoria (tela Limites) ou do teto individual acima; o campo "Horas
                          Aulas" é só a meta do lançamento.
                        </small>
                      </div>
                    )}

                  </>
                )}

                <label className={`um-switch ${formData.isActive ? 'um-switch--on' : ''}`}>
                  <input
                    type="checkbox"
                    checked={formData.isActive}
                    onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                  />
                  <span className="um-switch__track" aria-hidden="true">
                    <span className="um-switch__thumb" />
                  </span>
                  <span className="um-switch__text">
                    <strong>{formData.isActive ? 'Ativo' : 'Inativo'}</strong>
                    <span>
                      {formData.isActive
                        ? 'Entra no sistema, aparece no seletor do fiscal e nos relatórios.'
                        : 'Não consegue logar, some do seletor do fiscal e dos relatórios, e o lançamento de hora-aula passa a ser recusado com "Militar Inativo".'}
                    </span>
                  </span>
                </label>

                <div className="modal-actions">
                  <button type="button" className="btn-secondary" onClick={handleCloseModal}>
                    Cancelar
                  </button>
                  <button type="submit" className="btn-primary">
                    {editingUser ? 'Salvar Alterações' : 'Criar Usuário'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {showDeleteModal && userToDelete && (
          <div className="modal-overlay" onClick={closeDeleteModal}>
            <div className="modal" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>Excluir usuário</h2>
                <button
                  type="button"
                  className="modal-close"
                  onClick={closeDeleteModal}
                  disabled={deletingUser}
                >
                  ×
                </button>
              </div>

              <div className="confirmation-body">
                <p>
                  Tem certeza que deseja excluir o usuário{' '}
                  <strong>{userToDelete.name}</strong>?
                </p>
                <p>
                  Essa ação é permanente. Caso o militar precise voltar ao sistema,
                  será necessário cadastrá-lo novamente.
                </p>
              </div>

              <div className="modal-actions">
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={closeDeleteModal}
                  disabled={deletingUser}
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  className="btn-danger"
                  onClick={handleConfirmDelete}
                  disabled={deletingUser}
                >
                  {deletingUser ? 'Removendo...' : 'Sim, excluir'}
                </button>
              </div>
            </div>
          </div>
        )}

        {statusTarget && (
          <div className="modal-overlay" onClick={closeStatusModal}>
            <div className="modal modal-sm" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>{statusTarget.isActive ? 'Inativar cadastro' : 'Ativar cadastro'}</h2>
                <button
                  type="button"
                  className="modal-close"
                  onClick={closeStatusModal}
                  disabled={savingStatus}
                >
                  ×
                </button>
              </div>

              <div className="confirmation-body">
                {statusTarget.isActive ? (
                  <>
                    <p>
                      Inativar <strong>{statusTarget.name}</strong>?
                    </p>
                    <ul className="um-effects">
                      <li>Deixa de conseguir entrar no sistema.</li>
                      <li>Some do seletor de militares do fiscal.</li>
                      <li>Some dos relatórios do período.</li>
                      <li>
                        O lançamento de hora-aula para essa pessoa passa a ser recusado
                        com <strong>"Militar Inativo"</strong>.
                      </li>
                    </ul>
                    <p className="warning-text">
                      Os registros já lançados continuam no banco — inativar não apaga histórico.
                    </p>
                  </>
                ) : (
                  <>
                    <p>
                      Reativar <strong>{statusTarget.name}</strong>?
                    </p>
                    <ul className="um-effects">
                      <li>Volta a conseguir entrar no sistema.</li>
                      <li>Volta ao seletor do fiscal e aos relatórios.</li>
                      <li>O lançamento de hora-aula volta a ser aceito.</li>
                    </ul>
                  </>
                )}
              </div>

              <div className="modal-actions">
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={closeStatusModal}
                  disabled={savingStatus}
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  className={statusTarget.isActive ? 'btn-danger' : 'btn-primary'}
                  onClick={handleConfirmStatus}
                  disabled={savingStatus}
                >
                  {savingStatus
                    ? 'Salvando...'
                    : statusTarget.isActive
                      ? 'Sim, inativar'
                      : 'Sim, ativar'}
                </button>
              </div>
            </div>
          </div>
        )}

        {bulkAction && (
          <div className="modal-overlay" onClick={() => !applyingBulk && setBulkAction(null)}>
            <div className="modal modal-sm" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>
                  {bulkAction.efetivoCmcb
                    ? 'Marcar como efetivo do CMCB'
                    : 'Remover do efetivo do CMCB'}
                </h2>
                <button
                  type="button"
                  className="modal-close"
                  onClick={() => setBulkAction(null)}
                  disabled={applyingBulk}
                >
                  ×
                </button>
              </div>

              <div className="confirmation-body">
                <p>
                  <strong>{selecionados.length}</strong>{' '}
                  {selecionados.length === 1 ? 'pessoa será afetada' : 'pessoas serão afetadas'}.
                </p>
                <p>
                  {bulkAction.efetivoCmcb
                    ? 'Quem for marcado passa a ter teto de 48h no ciclo (dia 16 do mês anterior ao dia 15 do mês atual), entra na tela de Lançamento em massa e passa a sair no relatório mesmo sem cota de hora-aula.'
                    : 'Quem for removido volta ao teto padrão de 40h no ciclo. A cota individual de hora-aula não é alterada em nenhum dos dois casos.'}
                </p>
                <ul className="um-namelist">
                  {selecionados.slice(0, 12).map((u) => (
                    <li key={u.id}>
                      {u.militaryRank ? `${u.militaryRank} ` : ''}
                      {u.name}
                    </li>
                  ))}
                </ul>
                {selecionados.length > 12 && (
                  <p className="um-namelist__more">
                    + {selecionados.length - 12} outra(s) pessoa(s)
                  </p>
                )}
              </div>

              <div className="modal-actions">
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => setBulkAction(null)}
                  disabled={applyingBulk}
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  className="btn-primary"
                  onClick={applyBulkEfetivo}
                  disabled={applyingBulk}
                >
                  {applyingBulk
                    ? 'Aplicando...'
                    : bulkAction.efetivoCmcb
                      ? 'Sim, marcar'
                      : 'Sim, remover'}
                </button>
              </div>
            </div>
          </div>
        )}

        {syncModal.open && (
          <SyncModal
            state={syncModal.state}
            result={syncModal.result}
            errorMsg={syncModal.errorMsg}
            usersCount={users.filter((u) => u.matricula).length}
            onStart={startSync}
            onClose={closeSyncModal}
          />
        )}

        {facePhotosPerson && (
          <FacePhotosModal
            person={facePhotosPerson}
            canDelete
            onClose={() => setFacePhotosPerson(null)}
          />
        )}
      </div>
    </>
  );
};

/** ------------------------------------------------------------------
 * Sync Modal — 4 estados: idle | running | done | disabled | error
 * ------------------------------------------------------------------ */
function SyncModal({ state, result, errorMsg, usersCount, onStart, onClose }) {
  return (
    <div className="modal-overlay" onClick={state === 'running' ? undefined : onClose}>
      <div className="modal sync-modal" onClick={(e) => e.stopPropagation()}>
        <div className="sync-modal__hero">
          <div className={`sync-modal__icon sync-modal__icon--${state}`}>
            {state === 'running' ? (
              <div className="sync-spinner" aria-label="Sincronizando" />
            ) : state === 'done' ? (
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="20 6 9 17 4 12"></polyline>
              </svg>
            ) : state === 'disabled' ? (
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"></circle>
                <line x1="12" y1="8" x2="12" y2="12"></line>
                <line x1="12" y1="16" x2="12.01" y2="16"></line>
              </svg>
            ) : state === 'error' ? (
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"></circle>
                <line x1="15" y1="9" x2="9" y2="15"></line>
                <line x1="9" y1="9" x2="15" y2="15"></line>
              </svg>
            ) : (
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="23 4 23 10 17 10"></polyline>
                <polyline points="1 20 1 14 7 14"></polyline>
                <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"></path>
              </svg>
            )}
          </div>
          <h2 className="sync-modal__title">
            {state === 'running' && 'Sincronizando com o SABM…'}
            {state === 'done' && 'Sincronização concluída'}
            {state === 'disabled' && 'Integração desligada'}
            {state === 'error' && 'Falha na sincronização'}
            {state === 'idle' && 'Sincronizar com o SABM'}
          </h2>
          <p className="sync-modal__subtitle">
            {state === 'running' && 'Consultando pessoa_bm.pts_codigo pra cada militar cadastrado…'}
            {state === 'done' && 'Estado local atualizado com a fonte canônica do SABM.'}
            {state === 'disabled' && 'LEGACY_ADAPTER_URL não está setada no backend.'}
            {state === 'error' && 'Erro inesperado — nenhuma alteração foi feita.'}
            {state === 'idle' && 'Atualiza o campo isActive local com o pts_codigo canônico do CBMCE.'}
          </p>
        </div>

        <div className="sync-modal__body">
          {state === 'idle' && (
            <>
              <div className="sync-modal__info-card">
                <div className="sync-modal__info-row">
                  <span className="sync-modal__info-label">Militares com matrícula</span>
                  <span className="sync-modal__info-value">{usersCount}</span>
                </div>
                <div className="sync-modal__info-row">
                  <span className="sync-modal__info-label">Fonte canônica</span>
                  <span className="sync-modal__info-value sync-modal__info-value--muted">SABM · pessoa_bm.pts_codigo</span>
                </div>
                <div className="sync-modal__info-row">
                  <span className="sync-modal__info-label">Regra</span>
                  <span className="sync-modal__info-value sync-modal__info-value--muted">Ativo se pts_codigo ∈ {'{'}1, 5{'}'}</span>
                </div>
              </div>
              <div className="sync-modal__hint">
                <strong>⚠ Atenção:</strong> essa operação altera <code>isActive</code> de militares que estiverem inativos no CBMCE. Bloqueios de aula passam a valer imediatamente.
              </div>
            </>
          )}

          {state === 'running' && (
            <div className="sync-modal__progress">
              <div className="sync-modal__progress-bar">
                <div className="sync-modal__progress-fill" />
              </div>
              <p className="sync-modal__progress-hint">Isso pode levar alguns segundos dependendo de quantos militares estão cadastrados.</p>
            </div>
          )}

          {state === 'done' && result && (
            <SyncDoneView result={result} />
          )}

          {state === 'disabled' && (
            <div className="sync-modal__hint sync-modal__hint--warning">
              O backend ainda não tem <code>LEGACY_ADAPTER_URL</code> configurada. Sem essa env, a fonte canônica não é consultada — o sistema segue usando a marcação manual de <code>isActive</code> na tela de Usuários e as licenças cadastradas em <strong>/admin/licenses</strong>.
              <br /><br />
              <strong>Como ativar:</strong> <code>sudo k3s kubectl -n ponto set env deploy/backend LEGACY_ADAPTER_URL=…</code>
            </div>
          )}

          {state === 'error' && (
            <div className="sync-modal__hint sync-modal__hint--danger">
              <strong>Erro:</strong> {errorMsg}
            </div>
          )}
        </div>

        <div className="sync-modal__footer">
          {state === 'idle' && (
            <>
              <button className="btn-secondary" onClick={onClose}>Cancelar</button>
              <button className="btn-primary" onClick={onStart}>Iniciar sincronização</button>
            </>
          )}
          {state === 'running' && (
            <button className="btn-secondary" disabled>Aguardando SABM…</button>
          )}
          {(state === 'done' || state === 'disabled' || state === 'error') && (
            <button className="btn-primary" onClick={onClose}>Fechar</button>
          )}
        </div>
      </div>
    </div>
  );
}

function StatCard({ label, value, tone, hint }) {
  return (
    <div className={`sync-stat sync-stat--${tone}`}>
      <div className="sync-stat__value">{value}</div>
      <div className="sync-stat__label">{label}</div>
      {hint ? <div className="sync-stat__hint">{hint}</div> : null}
    </div>
  );
}

/** Vista rica do resultado do sync — painel de saúde + 5 seções expansíveis com busca. */
function SyncDoneView({ result }) {
  const total = result.totalUsersScanned || 0;
  const updatedN = result.updated?.length || 0;
  const alreadyN = result.alreadyInSync?.length || 0;
  const unreachableN = result.unreachable?.length || result.legacyUnreachable || 0;
  const skippedN = result.skipped?.length || result.skippedNoMatricula || 0;
  const errorsN = result.errors?.length || 0;
  const healthyN = updatedN + alreadyN;
  const healthPct = total > 0 ? Math.round((healthyN / total) * 100) : 0;
  const durationMs = result.durationMs || 0;
  const durationStr = durationMs < 1000
    ? `${durationMs} ms`
    : `${(durationMs / 1000).toFixed(1)}s`;

  return (
    <>
      {/* Painel de saúde */}
      <div className="sync-health">
        <div className="sync-health__ring">
          <svg viewBox="0 0 42 42" className="sync-health__ring-svg">
            <circle className="sync-health__ring-bg" cx="21" cy="21" r="15.915" fill="none" strokeWidth="4" />
            <circle
              className="sync-health__ring-fg"
              cx="21" cy="21" r="15.915" fill="none" strokeWidth="4"
              strokeDasharray={`${healthPct} ${100 - healthPct}`}
              strokeDashoffset="25"
              strokeLinecap="round"
            />
          </svg>
          <div className="sync-health__ring-label">
            <strong>{healthPct}%</strong>
            <span>em sync</span>
          </div>
        </div>
        <div className="sync-health__side">
          <p className="sync-health__title">
            <strong>{healthyN}</strong> de <strong>{total}</strong> militares em sincronia com o SABM
          </p>
          <p className="sync-health__meta">
            Consulta concluída em <strong>{durationStr}</strong> · fonte canônica: <code>sec_users</code> + <code>pessoa_bm</code>
          </p>
        </div>
      </div>

      {/* Stat cards resumo */}
      <div className="sync-stats">
        <StatCard label="Atualizados agora" value={updatedN} tone="success" hint={updatedN === 0 ? 'nada mudou' : 'gravado no banco'} />
        <StatCard label="Já em sincronia" value={alreadyN} tone="neutral" hint="tudo bate" />
        <StatCard label="Não achado no SABM" value={unreachableN} tone="warning" hint="matrícula sem cadastro" />
        <StatCard label="Sem matrícula" value={skippedN} tone="neutral" hint="admins/system" />
      </div>

      {errorsN > 0 && (
        <div className="sync-modal__hint sync-modal__hint--danger" style={{ marginTop: 12 }}>
          <strong>{errorsN} erro(s) durante o sync:</strong>{' '}
          {result.errors.slice(0, 5).map((e) => `${e.matricula}${e.name ? ` (${e.name.split(' ')[0]})` : ''}`).join(', ')}
          {errorsN > 5 && ` +${errorsN - 5} outros`}
        </div>
      )}

      {/* Seções expansíveis */}
      <div className="sync-sections">
        {updatedN > 0 && (
          <SyncSection
            id="updated"
            icon="✓"
            tone="success"
            title={`Atualizados agora (${updatedN})`}
            hint="Militares que tinham dado divergente e foram gravados"
            defaultOpen
            items={result.updated}
            renderRow={(u) => <UpdatedRow key={u.userId} u={u} />}
            searchOn={(u, q) =>
              u.name?.toLowerCase().includes(q) ||
              u.matricula?.toLowerCase().includes(q) ||
              (u.emailTo || '').toLowerCase().includes(q) ||
              (u.emailFrom || '').toLowerCase().includes(q)
            }
          />
        )}

        {alreadyN > 0 && (
          <SyncSection
            id="already"
            icon="✓"
            tone="muted"
            title={`Já em sincronia (${alreadyN})`}
            hint="Consultados e batendo com o SABM — nada mudou"
            items={result.alreadyInSync}
            renderRow={(u) => <SimpleRow key={u.userId} u={u} />}
            searchOn={(u, q) =>
              u.name?.toLowerCase().includes(q) ||
              u.matricula?.toLowerCase().includes(q)
            }
          />
        )}

        {unreachableN > 0 && (
          <SyncSection
            id="unreachable"
            icon="⚠"
            tone="warning"
            title={`Não encontrados no SABM (${unreachableN})`}
            hint="Matrícula não retornou pessoa_bm nem sec_users. Verifique cadastro."
            items={result.unreachable}
            renderRow={(u) => <SimpleRow key={u.userId} u={u} />}
            searchOn={(u, q) =>
              u.name?.toLowerCase().includes(q) ||
              u.matricula?.toLowerCase().includes(q)
            }
          />
        )}

        {skippedN > 0 && (
          <SyncSection
            id="skipped"
            icon="○"
            tone="muted"
            title={`Sem matrícula (${skippedN})`}
            hint="Admins do sistema e usuários que não têm bmCodigo — sync não se aplica"
            items={result.skipped}
            renderRow={(u) => (
              <div key={u.userId} className="sync-changes__row">
                <div className="sync-changes__name">
                  <strong>{u.name}</strong>
                  <span className="sync-changes__mat">{u.role} · {u.reason}</span>
                </div>
              </div>
            )}
            searchOn={(u, q) => u.name?.toLowerCase().includes(q) || u.role?.toLowerCase().includes(q)}
          />
        )}

        {errorsN > 0 && (
          <SyncSection
            id="errors"
            icon="✗"
            tone="danger"
            title={`Erros (${errorsN})`}
            hint="Exceções durante UPDATE — geralmente colisão de unique constraint (email)"
            defaultOpen
            items={result.errors}
            renderRow={(e, i) => (
              <div key={`${e.matricula}-${i}`} className="sync-changes__row">
                <div className="sync-changes__name">
                  <strong>{e.name || e.matricula}</strong>
                  <span className="sync-changes__mat">mat. {e.matricula}</span>
                </div>
                <div className="sync-changes__badges">
                  <span className="sync-badge sync-badge--error" title={e.error}>
                    {String(e.error).length > 40 ? String(e.error).slice(0, 40) + '…' : e.error}
                  </span>
                </div>
              </div>
            )}
            searchOn={(e, q) => (e.matricula + ' ' + (e.name || '') + ' ' + e.error).toLowerCase().includes(q)}
          />
        )}
      </div>
    </>
  );
}

function SyncSection({ id, icon, tone, title, hint, items, renderRow, searchOn, defaultOpen }) {
  const [open, setOpen] = useState(!!defaultOpen);
  const [query, setQuery] = useState('');
  const filtered = query
    ? items.filter((it) => searchOn(it, query.toLowerCase()))
    : items;
  const showMax = 100;

  return (
    <div className={`sync-section sync-section--${tone}`}>
      <button
        type="button"
        className="sync-section__head"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
      >
        <span className={`sync-section__icon sync-section__icon--${tone}`}>{icon}</span>
        <span className="sync-section__title">{title}</span>
        {hint ? <span className="sync-section__hint">{hint}</span> : null}
        <span className={`sync-section__chevron ${open ? 'sync-section__chevron--open' : ''}`}>▾</span>
      </button>
      {open && (
        <div className="sync-section__body">
          {items.length > 5 && (
            <div className="sync-section__search">
              <input
                type="text"
                placeholder="Buscar por nome, matrícula, email…"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
              />
              {query && (
                <span className="sync-section__search-count">{filtered.length} de {items.length}</span>
              )}
            </div>
          )}
          <div className="sync-changes__list">
            {filtered.slice(0, showMax).map((it, i) => renderRow(it, i))}
            {filtered.length === 0 && (
              <p className="sync-changes__more">Sem resultados pra "{query}"</p>
            )}
            {filtered.length > showMax && (
              <p className="sync-changes__more">
                Mostrando os primeiros {showMax} de {filtered.length} — refine a busca pra ver o resto.
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function UpdatedRow({ u }) {
  const statusChanged = u.from !== u.to;
  const emailChanged = !!u.emailTo && u.emailFrom !== u.emailTo;
  return (
    <div className="sync-changes__row">
      <div className="sync-changes__name">
        <strong>{u.name}</strong>
        <span className="sync-changes__mat">
          mat. {u.matricula}
          {u.ptsCodigo != null ? ` · pts_codigo ${u.ptsCodigo}` : ''}
          {emailChanged ? ` · ${u.emailFrom || '—'} → ${u.emailTo}` : ''}
        </span>
      </div>
      <div className="sync-changes__badges">
        {statusChanged && (
          <>
            <span className={`sync-badge ${u.from ? 'sync-badge--active' : 'sync-badge--inactive'}`}>
              {u.from ? 'Ativo' : 'Inativo'}
            </span>
            <span className="sync-changes__arrow">→</span>
            <span className={`sync-badge ${u.to ? 'sync-badge--active' : 'sync-badge--inactive'}`}>
              {u.to ? 'Ativo' : 'Inativo'}
            </span>
          </>
        )}
        {emailChanged && (
          <span className="sync-badge sync-badge--email">✉ email</span>
        )}
      </div>
    </div>
  );
}

function SimpleRow({ u }) {
  return (
    <div className="sync-changes__row">
      <div className="sync-changes__name">
        <strong>{u.name}</strong>
        <span className="sync-changes__mat">
          mat. {u.matricula}{u.militaryRank ? ` · ${u.militaryRank}` : ''}
        </span>
      </div>
    </div>
  );
}

export default UsersManagement;
